import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { toast } from "react-toastify";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import {
  getEnvGlobalSpecsList,
  getEnvGlobalSpecsByTest,
  postEnvGlobalSpecs,
} from "../../../../actions/specManager";
import {
  buildEnvGlobalSpecEditorRowsFromApi,
  buildEnvGlobalSpecsPostBody,
  mapEnvGlobalSpecsPointsFromApi,
  formatSpec,
  handleCheckUnit,
  checkIfSpecsInvalid,
  globalTestSpecEditorHasMrlCountryListData,
  globalTestSpecMrlMissingSelectedMarkets,
  globalTestSpecEditorRowsMatchForDraft,
  GLOBAL_TEST_SPEC_MODE,
  SPEC_FILTER_VALUES,
  SPEC_TYPE,
  canEditSpecs,
  mapEnvLocationsToProductShape,
} from "../utils/utils";
import { DiscardChangesModal } from "../shared/specManagerUi";
import TestSpecListPanel from "../testSpec/TestSpecListPanel";
import TestSpecOverview from "../testSpec/TestSpecOverview";
import EnvTestSpecEditor from "./EnvTestSpecEditor";
import SaveSuccessModal from "../testSpec/SaveSuccessModal";

const sortedLocationIds = (locations) => [
  ...locations.map((row) => row.location_id).filter(Boolean),
]
  .map((id) => String(id))
  .sort();

export default function EnvTestSpecManager({
  customer,
  identifierFields,
  onEditModeChange,
}) {
  const [mode, setMode] = useState(GLOBAL_TEST_SPEC_MODE.LIST);
  const [testRows, setTestRows] = useState([]);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [appliedSearchValue, setAppliedSearchValue] = useState("");
  const [activeFilter, setActiveFilter] = useState(SPEC_FILTER_VALUES.ALL);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPageLink, setNextPageLink] = useState("");
  const [selectedTestName, setSelectedTestName] = useState("");
  const [testSpecStatus, setTestSpecStatus] = useState("added");
  const [specRows, setSpecRows] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loadingEditData, setLoadingEditData] = useState(false);
  const [detailLoadFailed, setDetailLoadFailed] = useState(false);
  const [specDataChanged, setSpecDataChanged] = useState(false);
  const [step1Baseline, setStep1Baseline] = useState(null);
  const [isPreviewStep, setIsPreviewStep] = useState(false);
  const [locationSearchValue, setLocationSearchValue] = useState("");
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessModal, setSaveSuccessModal] = useState({
    open: false,
    testDisplayName: "",
    saveAction: "update",
    affectedProductCount: 0,
  });

  const abortControllerRef = useRef(null);
  const canEdit = canEditSpecs();

  const seedStep1Baseline = (locationsSnapshot, specSnapshot) => {
    setStep1Baseline({
      locations: cloneDeep(locationsSnapshot),
      spec: cloneDeep(specSnapshot),
    });
    setSpecDataChanged(false);
  };

  const hasDraftChanges = () => {
    if (!step1Baseline) return false;
    const baselineIds = sortedLocationIds(step1Baseline.locations);
    const currentIds = sortedLocationIds(selectedLocations);
    const locationsChanged = !isEqual(baselineIds, currentIds);
    return locationsChanged || specDataChanged;
  };

  const fetchTestList = async ({
    filter = activeFilter,
    search = appliedSearchValue,
    page = "1",
    append = false,
  } = {}) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    if (append) setLoadingMore(true);
    else setLoading(true);

    const response = await getEnvGlobalSpecsList(
      { filter, search, page },
      abortController.signal,
      customer,
    );

    if (response.canceled) return;

    if (response.success && Array.isArray(response.result)) {
      setTestRows((previous) => (append ? [...previous, ...response.result] : response.result));
      setNextPageLink(response.nextPageLink);
    } else if (!append) {
      setTestRows([]);
      setNextPageLink("");
      if (!response.canceled && response.message) {
        toast.error(response.message);
      }
    }

    setLoading(false);
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchTestList();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const isEditing = mode !== GLOBAL_TEST_SPEC_MODE.LIST;
    onEditModeChange(isEditing);
  }, [mode, onEditModeChange]);

  const openTestEditor = async (testName, status) => {
    setSelectedTestName(testName);
    setTestSpecStatus(status === "pending" ? "pending" : "added");
    setMode(GLOBAL_TEST_SPEC_MODE.VIEW);
    setDetailLoadFailed(false);
    setLoadingEditData(true);
    setSpecRows([]);
    setSelectedLocations([]);
    setStep1Baseline(null);
    setIsPreviewStep(false);
    setLocationSearchValue("");

    const response = await getEnvGlobalSpecsByTest(
      { test_display_name: testName },
      customer,
    );

    if (!response.success || !response.result) {
      toast.error(response.message);
      setDetailLoadFailed(true);
      setLoadingEditData(false);
      return;
    }

    const apiResult = response.result;
    const locationRows = mapEnvGlobalSpecsPointsFromApi(
      apiResult.locations,
      identifierFields,
    );
    setSelectedLocations(locationRows);

    const specToUse = buildEnvGlobalSpecEditorRowsFromApi(apiResult, testName);
    setSpecRows(specToUse);

    setLoadingEditData(false);
  };

  const handleOpenEditMode = () => {
    if (!canEdit) return;
    setLocationSearchValue("");
    seedStep1Baseline(selectedLocations, specRows);
    setMode(testSpecStatus === "pending" ? GLOBAL_TEST_SPEC_MODE.ADD : GLOBAL_TEST_SPEC_MODE.EDIT);
  };

  const handleSpecRowsChange = (updatedRows) => {
    const nextRows = cloneDeep(updatedRows);
    setSpecRows(nextRows);
    if (step1Baseline) {
      const matchesBaseline = globalTestSpecEditorRowsMatchForDraft(nextRows, step1Baseline.spec);
      setSpecDataChanged(!matchesBaseline);
    }
  };

  const handleSelectedLocationsChange = (locations) => {
    setSelectedLocations(locations);
    if (step1Baseline) {
      const baselineIds = sortedLocationIds(step1Baseline.locations);
      const currentIds = sortedLocationIds(locations);
      const locationsChanged = !isEqual(baselineIds, currentIds);
      const specMatchesBaseline = globalTestSpecEditorRowsMatchForDraft(specRows, step1Baseline.spec);
      setSpecDataChanged(locationsChanged || !specMatchesBaseline);
    }
  };

  const handleSaveClick = async () => {
    if (!canEdit) {
      toast.error("You do not have permission to edit test specs.");
      return;
    }
    if (globalTestSpecMrlMissingSelectedMarkets(specRows)) {
      toast.error("Please select at least one country for MRL tests.");
      return;
    }
    if (checkIfSpecsInvalid(specRows)) {
      toast.error("Invalid or missing value(s).");
      return;
    }
    if (selectedLocations.length === 0) {
      toast.error("Please select at least one location.");
      return;
    }

    setIsSaving(true);
    const formattedRows = handleCheckUnit(formatSpec(specRows));
    const firstSpec = formattedRows[0];
    if (typeof firstSpec === "string") {
      toast.error(firstSpec);
      setIsSaving(false);
      return;
    }

    const payload = buildEnvGlobalSpecsPostBody({
      testDisplayName: selectedTestName,
      formattedFirstSpec: firstSpec,
      locationIdList: selectedLocations.map((row) => row.location_id).filter(Boolean),
      includeMrlCountriesList: globalTestSpecEditorHasMrlCountryListData(specRows),
    });

    const result = await postEnvGlobalSpecs(payload, customer);
    setIsSaving(false);

    if (!result.success) {
      toast.error(result.message);
      return;
    }

    setSaveSuccessModal({
      open: true,
      testDisplayName: selectedTestName,
      saveAction: mode === GLOBAL_TEST_SPEC_MODE.ADD ? "add" : "update",
      affectedProductCount: selectedLocations.length,
    });
  };

  const handlePreviewStepChange = (nextIsPreviewStep) => {
    if (nextIsPreviewStep) {
      setLocationSearchValue("");
    }
    setIsPreviewStep(nextIsPreviewStep);
  };

  const exitToList = () => {
    setMode(GLOBAL_TEST_SPEC_MODE.LIST);
    setSelectedTestName("");
    setSpecRows([]);
    setSelectedLocations([]);
    setSpecDataChanged(false);
    setStep1Baseline(null);
    setIsPreviewStep(false);
    setLocationSearchValue("");
    setDetailLoadFailed(false);
  };

  const handleSaveSuccessModalClose = () => {
    setSaveSuccessModal({
      open: false,
      testDisplayName: "",
      saveAction: "update",
      affectedProductCount: 0,
    });
    exitToList();
    fetchTestList();
  };

  const handleBack = () => {
    if (hasDraftChanges()) {
      setShowDiscardModal(true);
    } else {
      exitToList();
    }
  };

  const handleLoadMore = () => {
    if (!nextPageLink || loadingMore || loading) return;
    const page = nextPageLink.split("page=")[1].split("&")[0];
    fetchTestList({ page, append: true });
  };

  const handleSearchSubmit = (submittedSearch) => {
    const nextSearch = submittedSearch != null ? submittedSearch : searchInputValue;
    setSearchInputValue(nextSearch);
    setAppliedSearchValue(nextSearch);
    fetchTestList({ search: nextSearch });
  };

  const handleSearchInputChange = (nextInputValue) => {
    setSearchInputValue(nextInputValue);
    if (nextInputValue === "" && appliedSearchValue !== "") {
      setAppliedSearchValue("");
      fetchTestList({ search: "" });
    }
  };

  if (mode === GLOBAL_TEST_SPEC_MODE.LIST) {
    return (
      <TestSpecListPanel
        testRows={testRows}
        loading={loading}
        loadingMore={loadingMore}
        searchInputValue={searchInputValue}
        activeFilter={activeFilter}
        onSearchInputChange={handleSearchInputChange}
        onSearchSubmit={handleSearchSubmit}
        onFilterChange={(filter) => {
          setActiveFilter(filter);
          fetchTestList({ filter });
        }}
        onTestRowClick={openTestEditor}
        onLoadMore={handleLoadMore}
      />
    );
  }

  if (mode === GLOBAL_TEST_SPEC_MODE.VIEW) {
    return (
      <>
        {loadingEditData ? (
          <div className="SpecManager_LoadingCenter"><Spin /></div>
        ) : (
          <TestSpecOverview
            testName={selectedTestName}
            specRows={specRows}
            products={mapEnvLocationsToProductShape(selectedLocations, identifierFields)}
            identifierFields={identifierFields}
            testSpecStatus={testSpecStatus}
            loadFailed={detailLoadFailed}
            specType={SPEC_TYPE.ENVIRONMENT}
            onEditClick={handleOpenEditMode}
            onBack={handleBack}
          />
        )}
        <DiscardChangesModal
          open={showDiscardModal}
          onCancel={() => setShowDiscardModal(false)}
          onConfirm={() => {
            setShowDiscardModal(false);
            exitToList();
          }}
        />
      </>
    );
  }

  return (
    <>
      {loadingEditData ? (
        <div className="SpecManager_LoadingCenter"><Spin /></div>
      ) : (
        <EnvTestSpecEditor
          customer={customer}
          identifierFields={identifierFields}
          testName={selectedTestName}
          specRows={specRows}
          selectedLocations={selectedLocations}
          isPreviewStep={isPreviewStep}
          hasDraftChanges={hasDraftChanges()}
          locationSearchValue={locationSearchValue}
          onLocationSearchChange={setLocationSearchValue}
          onSpecRowsChange={handleSpecRowsChange}
          onSelectedLocationsChange={handleSelectedLocationsChange}
          onPreviewStepChange={handlePreviewStepChange}
          onSaveClick={handleSaveClick}
          onBack={handleBack}
          saving={isSaving}
        />
      )}

      <SaveSuccessModal
        open={saveSuccessModal.open}
        testDisplayName={saveSuccessModal.testDisplayName}
        saveAction={saveSuccessModal.saveAction}
        affectedProductCount={saveSuccessModal.affectedProductCount}
        onClose={handleSaveSuccessModalClose}
      />

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={() => setShowDiscardModal(false)}
        onConfirm={() => {
          setShowDiscardModal(false);
          exitToList();
        }}
      />
    </>
  );
}
